import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { fetchReplacementAlternatives } from '../../pages/ExperimentManagement/services/experimentManagementApi';

/**
 * Context for choosing what replaces a product, the same on every screen that offers it:
 * - the machine's quadrant (how much it sells and its margin vs similar machines of its sector, from
 *   the orchestrator's nightly run), as a plain phrase;
 * - the incoming options by goal: "Más margen", whose first one is the orchestrator's pick and stays
 *   the recommendation, and "Más volumen" (extra units). Up to 3 each, a product can be in both.
 */

const QUADRANTS = ['TOP', 'aumentar_margen', 'aumentar_rotacion', 'low'];

/** `tooltip={false}`: just the phrase, for the small pickers where it is already enough. */
export const MachineQuadrant = ({ quadrant, sx, tooltip = true }) => {
  const { t } = useTranslation();
  if (!QUADRANTS.includes(quadrant)) return null;
  const label = (
    <Typography component="span" sx={{ fontSize: '11px', color: 'text.secondary', cursor: tooltip ? 'help' : 'default', ...sx }}>
      {t('quadrant_machine', { label: t(`quadrant_${quadrant}`) })}
    </Typography>
  );
  return tooltip ? <Tooltip title={t('quadrant_tooltip')}>{label}</Tooltip> : label;
};

/** Both lists as one, for a select or autocomplete: `group` is 'margin' | 'volume', `key` is unique. */
export const alternativeOptions = ({ margin = [], volume = [] } = {}) => [
  ...margin.map((o, i) => ({ ...o, group: 'margin', recommended: i === 0, key: `margin-${o.productId}` })),
  ...volume.map((o) => ({ ...o, group: 'volume', recommended: false, key: `volume-${o.productId}` })),
];

export const alternativeGroupLabel = (t, group) => t(group === 'volume' ? 'alternatives_volume' : 'alternatives_margin');

/** The figure an option is listed by: its margin gain (%, or €/year when the lane makes no money
 *  today) for margin, extra units per month for volume. */
export const AlternativeMeta = ({ option }) => {
  const { t, i18n } = useTranslation();
  let text;
  if (option.group === 'volume') {
    const u = Number(option.unitsMonth) || 0;
    text = t('alternatives_unitsMonth', { units: u.toLocaleString(i18n.language, { maximumFractionDigits: u >= 10 ? 0 : 1 }) });
  } else if (option.improvementPercentage != null) {
    text = `+${Math.round(option.improvementPercentage)}%`;
  } else {
    text = t('alternatives_marginYear', { eur: Math.round(Number(option.marginEurYear) || 0) });
  }
  return (
    <Typography component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#15803d', whiteSpace: 'nowrap' }}>
      {text}
    </Typography>
  );
};

/** One option in a list: the recommendation marked like the AI pick elsewhere, name, its figure. */
export const AlternativeOptionRow = ({ option }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {option.recommended && (
        <Tooltip title={t('alternatives_recommended')}>
          <AutoAwesomeIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
        </Tooltip>
      )}
      {/* Long names wrap, as the plain candidate lists always did, instead of being cut */}
      <Typography sx={{ fontSize: '12px', flex: 1, minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{option.productName}</Typography>
      <AlternativeMeta option={option} />
    </Box>
  );
};

// Per experiment, for the session: alternatives are saved with the proposal and do not change.
const cache = new Map();

/** A replacement experiment's `{ quadrant, margin, volume }`, fetched when `enabled` (e.g. when its
 *  selector opens). `data` is null until it arrives. */
export const useReplacementAlternatives = (clientId, experimentId, enabled = true) => {
  const key = `${clientId}|${experimentId}`;
  // Tagged with its key, so a component reused for another experiment never shows the previous one's.
  const [data, setData] = useState(() => ({ key, value: cache.get(key)?.value ?? null }));
  useEffect(() => {
    if (!enabled || !clientId || !experimentId) return undefined;
    let cancelled = false;
    let entry = cache.get(key);
    if (!entry) {
      entry = { promise: fetchReplacementAlternatives(clientId, experimentId) };
      entry.promise.then((value) => { entry.value = value; });
      cache.set(key, entry);
    }
    entry.promise.then((value) => { if (!cancelled) setData({ key, value }); });
    return () => { cancelled = true; };
  }, [key, enabled, clientId, experimentId]);
  return data.key === key ? data.value : (cache.get(key)?.value ?? null);
};
